import { useCallback, useEffect, useRef } from 'react';

export const defaultDirtyDraftGuardMessage = 'You have unsaved draft changes. Discard them and leave?';

export type DirtyDraftConfirm = (message: string) => boolean;
export type DirtyDraftGetter = () => boolean;

export type DirtyDraftGuardOptions = {
  dirty: boolean | DirtyDraftGetter;
  message?: string;
  confirm?: DirtyDraftConfirm;
};

export type DirtyDraftGuard = {
  confirmLeave: () => boolean;
  guardAction: (action: () => void) => boolean;
  guardDrawerClose: (closeDrawer: () => void) => boolean;
  guardTabSwitch: (nextTab: string, selectTab: (tab: string) => void) => boolean;
};

export type BeforeUnloadEventLike = {
  preventDefault?: () => void;
  returnValue?: string;
};

export type EventTargetLike<TEvent = Event> = {
  addEventListener: (type: string, listener: (event: TEvent) => void) => void;
  removeEventListener: (type: string, listener: (event: TEvent) => void) => void;
};

export type HashAnchorClickTarget = EventTargetLike<MouseEvent>;

export type HashRouteLeaveTarget = EventTargetLike<Event> & {
  location: { hash: string };
  history?: { replaceState?: (data: unknown, unused: string, url?: string | URL | null) => void };
};

export function createDirtyDraftGuard(options: DirtyDraftGuardOptions): DirtyDraftGuard {
  const message = options.message ?? defaultDirtyDraftGuardMessage;
  const confirm = options.confirm ?? defaultConfirm;
  const confirmLeave = () => !readDirty(options.dirty) || confirm(message);
  const guardAction = (action: () => void) => {
    if (!confirmLeave()) return false;
    action();
    return true;
  };

  return {
    confirmLeave,
    guardAction,
    guardDrawerClose: guardAction,
    guardTabSwitch: (nextTab, selectTab) => guardAction(() => selectTab(nextTab)),
  };
}

export function registerBeforeUnloadGuard({
  getDirty,
  message = defaultDirtyDraftGuardMessage,
  target = getWindowTarget(),
}: {
  getDirty: DirtyDraftGetter;
  message?: string;
  target?: EventTargetLike<BeforeUnloadEventLike>;
}) {
  if (!target) return () => undefined;
  const handleBeforeUnload = (event: BeforeUnloadEventLike) => {
    if (!getDirty()) return;
    event.preventDefault?.();
    event.returnValue = message;
    return message;
  };
  target.addEventListener('beforeunload', handleBeforeUnload);
  return () => target.removeEventListener('beforeunload', handleBeforeUnload);
}

export function registerHashRouteLeaveGuard({
  getDirty,
  message = defaultDirtyDraftGuardMessage,
  confirm = defaultConfirm,
  target = getWindowHashTarget(),
  onBlocked,
}: {
  getDirty: DirtyDraftGetter;
  message?: string;
  confirm?: DirtyDraftConfirm;
  target?: HashRouteLeaveTarget;
  onBlocked?: (nextHash: string, previousHash: string) => void;
}) {
  if (!target) return () => undefined;
  let previousHash = target.location.hash;
  let reverting = false;

  const handleHashChange = (event: Event) => {
    const nextHash = target.location.hash;
    if (reverting) {
      reverting = false;
      previousHash = nextHash;
      return;
    }
    if (!getDirty() || confirm(message)) {
      previousHash = nextHash;
      return;
    }
    event.preventDefault?.();
    onBlocked?.(nextHash, previousHash);
    if (target.history?.replaceState) {
      target.history.replaceState(null, '', previousHash || '#/');
    } else {
      reverting = true;
      target.location.hash = previousHash;
    }
  };

  target.addEventListener('hashchange', handleHashChange);
  return () => target.removeEventListener('hashchange', handleHashChange);
}

export function registerHashAnchorClickGuard({
  getDirty,
  message = defaultDirtyDraftGuardMessage,
  confirm = defaultConfirm,
  target = getDocumentTarget(),
}: {
  getDirty: DirtyDraftGetter;
  message?: string;
  confirm?: DirtyDraftConfirm;
  target?: HashAnchorClickTarget;
}) {
  if (!target) return () => undefined;
  const handleClick = (event: MouseEvent) => {
    if (!getDirty() || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const element = typeof Element !== 'undefined' && event.target instanceof Element ? event.target : null;
    const link = element?.closest('a[href^="#"]') as HTMLAnchorElement | null;
    const nextHash = link?.getAttribute('href') ?? '';
    const currentHash = typeof window === 'undefined' ? '' : window.location.hash || '#/';
    if (!nextHash || nextHash === currentHash) return;
    if (confirm(message)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  target.addEventListener('click', handleClick);
  return () => target.removeEventListener('click', handleClick);
}

export function useDirtyDraftGuard({ dirty, message, confirm }: DirtyDraftGuardOptions): DirtyDraftGuard {
  const dirtyRef = useRef(readDirty(dirty));
  dirtyRef.current = readDirty(dirty);
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;
  const messageValue = message ?? defaultDirtyDraftGuardMessage;

  useEffect(() => registerBeforeUnloadGuard({
    getDirty: () => dirtyRef.current,
    message: messageValue,
  }), [messageValue]);

  useEffect(() => registerHashRouteLeaveGuard({
    getDirty: () => dirtyRef.current,
    message: messageValue,
    confirm: promptMessage => (confirmRef.current ?? defaultConfirm)(promptMessage),
  }), [messageValue]);

  useEffect(() => registerHashAnchorClickGuard({
    getDirty: () => dirtyRef.current,
    message: messageValue,
    confirm: promptMessage => (confirmRef.current ?? defaultConfirm)(promptMessage),
  }), [messageValue]);

  const confirmLeave = useCallback(() => createDirtyDraftGuard({
    dirty: () => dirtyRef.current,
    message: messageValue,
    confirm: promptMessage => (confirmRef.current ?? defaultConfirm)(promptMessage),
  }).confirmLeave(), [messageValue]);

  const guardAction = useCallback((action: () => void) => {
    if (!confirmLeave()) return false;
    action();
    return true;
  }, [confirmLeave]);

  return {
    confirmLeave,
    guardAction,
    guardDrawerClose: guardAction,
    guardTabSwitch: (nextTab, selectTab) => guardAction(() => selectTab(nextTab)),
  };
}

function readDirty(dirty: boolean | DirtyDraftGetter) {
  return typeof dirty === 'function' ? dirty() : dirty;
}

function defaultConfirm(message: string) {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return false;
  return window.confirm(message);
}

function getWindowTarget(): EventTargetLike<BeforeUnloadEventLike> | undefined {
  if (typeof window === 'undefined') return undefined;
  return window as unknown as EventTargetLike<BeforeUnloadEventLike>;
}

function getWindowHashTarget(): HashRouteLeaveTarget | undefined {
  if (typeof window === 'undefined') return undefined;
  return window;
}

function getDocumentTarget(): HashAnchorClickTarget | undefined {
  if (typeof document === 'undefined') return undefined;
  return document;
}
